<script setup lang="ts">
import { computed, ref } from 'vue';
import { Calendar, Check, MapPin, Pencil, Settings, X } from 'lucide-vue-next';
import MailboxCard from '@/components/MailboxCard.vue';
import { useFamily } from '@/composables/useFamily';
import { useNavigation } from '@/composables/useNavigation';
import type { PlayRequest } from '@/types/domain';

const {
  pendingMessages,
  pendingSenderParentPlayRequests,
  receiverParentPlayRequests,
  selectedChild,
  setMessageStatus,
  setPlayRequestStatus,
  updatePlayRequestForParent
} = useFamily();
const { navigate } = useNavigation();

const editingPlayRequestId = ref('');
const playRequestForms = ref<Record<string, {
  parentAdjustedPlace: string;
  parentAdjustedDate: string;
  parentAdjustedTimeRange: PlayRequest['parentAdjustedTimeRange'];
  parentAdjustedExactTime: string;
  parentMessage: string;
  parentCompanionNote: string;
  parentPhone: string;
}>>({});

const totalOpenApprovals = computed(
  () => pendingMessages.value.length + pendingSenderParentPlayRequests.value.length + receiverParentPlayRequests.value.length
);

function childName(id: string) {
  return id === selectedChild.value.id ? selectedChild.value.firstName : 'Ein Kind';
}

function ensurePlayRequestForm(request: PlayRequest) {
  if (playRequestForms.value[request.id]) return;
  playRequestForms.value = {
    ...playRequestForms.value,
    [request.id]: {
      parentAdjustedPlace: request.parentAdjustedPlace || request.requestedPlaceLabel,
      parentAdjustedDate: request.parentAdjustedDate,
      parentAdjustedTimeRange: request.parentAdjustedTimeRange || 'Nachmittag',
      parentAdjustedExactTime: request.parentAdjustedExactTime,
      parentMessage: request.parentMessage,
      parentCompanionNote: request.parentCompanionNote,
      parentPhone: request.parentPhone
    }
  };
}

function openAdjustForm(request: PlayRequest) {
  ensurePlayRequestForm(request);
  editingPlayRequestId.value = request.id;
}

async function savePlayRequestAdjustments(request: PlayRequest) {
  const form = playRequestForms.value[request.id];
  if (!form) return;
  await updatePlayRequestForParent(request.id, form);
  editingPlayRequestId.value = '';
}

async function approvePlayRequest(request: PlayRequest) {
  const form = playRequestForms.value[request.id];
  if (form) await updatePlayRequestForParent(request.id, form);
  await setPlayRequestStatus(request.id, 'sent_to_receiver_parent');
}

async function rejectPlayRequest(request: PlayRequest) {
  await setPlayRequestStatus(request.id, 'rejected_by_sender_parent');
}

async function updateReceiverPlayRequestStatus(request: PlayRequest, status: PlayRequest['status']) {
  await setPlayRequestStatus(request.id, status);
}

async function approveMessage(messageId: string) {
  await setMessageStatus(messageId, 'approved');
}

async function rejectMessage(messageId: string) {
  await setMessageStatus(messageId, 'rejected');
}
</script>

<template>
  <section class="mx-auto max-w-6xl px-4 py-6 pb-28 sm:px-6 md:pb-10">
    <h1 class="text-4xl font-black text-heart-berry md:text-6xl">Elternfreigaben</h1>
    <p class="mt-4 max-w-2xl text-lg font-semibold leading-8 text-heart-ink">
      Inhalte verlassen die Familie erst, nachdem ein Elternteil sie geprueft und freigegeben hat.
    </p>
    <button
      class="focus-ring mt-5 inline-flex min-h-12 items-center gap-2 rounded-full bg-white px-5 font-black text-heart-ink shadow-sm"
      type="button"
      @click="navigate('parent-settings')"
    >
      <Settings class="h-5 w-5 text-heart-coral" aria-hidden="true" />
      Zum Elternbereich
    </button>
    <div class="mt-8 grid gap-5">
      <div v-if="totalOpenApprovals === 0" class="rounded-[28px] bg-white p-6 text-xl font-black text-heart-leaf shadow-soft">
        Alle offenen Briefe sind geprueft.
      </div>

      <article
        v-for="request in pendingSenderParentPlayRequests"
        :key="request.id"
        class="grid gap-4 rounded-[30px] bg-white/70 p-4 shadow-soft lg:grid-cols-[1fr_auto]"
      >
        <div class="rounded-[28px] bg-white p-5 shadow-soft">
          <p class="text-sm font-black uppercase tracking-[0.16em] text-heart-leaf">Spielanfrage</p>
          <h2 class="mt-2 text-2xl font-black text-heart-ink">
            {{ childName(request.senderChildId) }} moechte {{ request.receiverChildId.replace('child-', '') }} zum Spielen fragen
          </h2>
          <div class="mt-5 grid gap-3 md:grid-cols-2">
            <p class="inline-flex items-center gap-3 rounded-2xl bg-heart-cream px-4 py-3 text-lg font-black text-heart-ink">
              <MapPin class="h-6 w-6 text-heart-coral" aria-hidden="true" />
              Ort: {{ request.requestedPlaceLabel }}
            </p>
            <p class="inline-flex items-center gap-3 rounded-2xl bg-heart-cream px-4 py-3 text-lg font-black text-heart-ink">
              <Calendar class="h-6 w-6 text-heart-leaf" aria-hidden="true" />
              Zeitwunsch: {{ request.requestedTimeLabel }}
            </p>
          </div>
          <p class="mt-4 rounded-full bg-amber-100 px-4 py-2 text-sm font-black text-amber-800">Wartet auf Freigabe</p>

          <form
            v-if="editingPlayRequestId === request.id && playRequestForms[request.id]"
            class="mt-5 grid gap-4 rounded-[24px] bg-heart-cream p-4"
            @submit.prevent="savePlayRequestAdjustments(request)"
          >
            <label class="block text-sm font-black text-heart-ink">
              Ort
              <input v-model="playRequestForms[request.id].parentAdjustedPlace" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" />
            </label>
            <div class="grid gap-4 md:grid-cols-3">
              <label class="block text-sm font-black text-heart-ink">
                Datum
                <input v-model="playRequestForms[request.id].parentAdjustedDate" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" type="date" />
              </label>
              <label class="block text-sm font-black text-heart-ink">
                Zeitraum
                <select v-model="playRequestForms[request.id].parentAdjustedTimeRange" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4">
                  <option value="Vormittag">Vormittag</option>
                  <option value="Nachmittag">Nachmittag</option>
                  <option value="Abend">Abend</option>
                </select>
              </label>
              <label class="block text-sm font-black text-heart-ink">
                Uhrzeit optional
                <input v-model="playRequestForms[request.id].parentAdjustedExactTime" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" type="time" />
              </label>
            </div>
            <label class="block text-sm font-black text-heart-ink">
              Nachricht an Eltern
              <textarea v-model="playRequestForms[request.id].parentMessage" class="focus-ring mt-2 min-h-24 w-full rounded-2xl border border-orange-100 px-4 py-3" />
            </label>
            <div class="grid gap-4 md:grid-cols-2">
              <label class="block text-sm font-black text-heart-ink">
                Begleitperson / Hinweis
                <input v-model="playRequestForms[request.id].parentCompanionNote" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" />
              </label>
              <label class="block text-sm font-black text-heart-ink">
                Telefonnummer optional
                <input v-model="playRequestForms[request.id].parentPhone" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" />
              </label>
            </div>
            <button class="focus-ring min-h-12 rounded-2xl bg-heart-leaf px-5 font-black text-white" type="submit">
              Anpassung speichern
            </button>
          </form>
        </div>
        <div class="grid grid-cols-2 gap-3 lg:w-56 lg:grid-cols-1">
          <button class="focus-ring inline-flex min-h-16 items-center justify-center gap-2 rounded-2xl bg-heart-leaf px-5 font-black text-white" type="button" @click="approvePlayRequest(request)">
            <Check class="h-6 w-6" aria-hidden="true" />
            Freigeben
          </button>
          <button class="focus-ring inline-flex min-h-16 items-center justify-center gap-2 rounded-2xl bg-white px-5 font-black text-heart-ink" type="button" @click="openAdjustForm(request)">
            <Pencil class="h-6 w-6" aria-hidden="true" />
            Anpassen
          </button>
          <button class="focus-ring inline-flex min-h-16 items-center justify-center gap-2 rounded-2xl bg-rose-700 px-5 font-black text-white" type="button" @click="rejectPlayRequest(request)">
            <X class="h-6 w-6" aria-hidden="true" />
            Ablehnen
          </button>
        </div>
      </article>

      <article
        v-for="request in receiverParentPlayRequests"
        :key="`receiver-${request.id}`"
        class="grid gap-4 rounded-[30px] bg-white/70 p-4 shadow-soft lg:grid-cols-[1fr_auto]"
      >
        <div class="rounded-[28px] bg-white p-5 shadow-soft">
          <p class="text-sm font-black uppercase tracking-[0.16em] text-heart-leaf">Anfrage anderer Eltern</p>
          <h2 class="mt-2 text-2xl font-black text-heart-ink">Spielanfrage von {{ childName(request.senderChildId) }}</h2>
          <div class="mt-5 grid gap-3 md:grid-cols-2">
            <p class="rounded-2xl bg-heart-cream px-4 py-3 text-lg font-black text-heart-ink">Ort: {{ request.parentAdjustedPlace || request.requestedPlaceLabel }}</p>
            <p class="rounded-2xl bg-heart-cream px-4 py-3 text-lg font-black text-heart-ink">Zeit: {{ request.parentAdjustedDate || request.requestedTimeLabel }} {{ request.parentAdjustedTimeRange }}</p>
          </div>
          <p class="mt-4 rounded-[22px] bg-heart-cream px-4 py-3 font-bold leading-7 text-heart-ink">{{ request.parentMessage }}</p>
        </div>
        <div class="grid grid-cols-2 gap-3 lg:w-64 lg:grid-cols-1">
          <button class="focus-ring inline-flex min-h-16 items-center justify-center gap-2 rounded-2xl bg-heart-leaf px-5 font-black text-white" type="button" @click="updateReceiverPlayRequestStatus(request, 'confirmed')">
            <Check class="h-6 w-6" aria-hidden="true" />
            Passt
          </button>
          <button class="focus-ring inline-flex min-h-16 items-center justify-center gap-2 rounded-2xl bg-white px-5 font-black text-heart-ink" type="button" @click="updateReceiverPlayRequestStatus(request, 'counter_proposal')">
            <Pencil class="h-6 w-6" aria-hidden="true" />
            Anderen Vorschlag machen
          </button>
          <button class="focus-ring inline-flex min-h-16 items-center justify-center gap-2 rounded-2xl bg-rose-700 px-5 font-black text-white" type="button" @click="updateReceiverPlayRequestStatus(request, 'declined')">
            <X class="h-6 w-6" aria-hidden="true" />
            Geht leider nicht
          </button>
        </div>
      </article>

      <div v-for="message in pendingMessages" :key="message.id" class="grid gap-4 rounded-[30px] bg-white/70 p-4 shadow-soft lg:grid-cols-[1fr_auto]">
        <div class="grid gap-4">
          <MailboxCard :message="message" />

          <article class="rounded-[28px] bg-white p-5 shadow-soft" aria-label="Inhalt vor Freigabe pruefen">
            <h2 class="text-xl font-black text-heart-ink">Inhalt pruefen</h2>

            <div v-if="message.type === 'drawing' && message.mediaUrl" class="mt-4 rounded-[24px] bg-heart-cream p-4">
              <img class="max-h-[32rem] w-full rounded-2xl bg-white object-contain shadow-inner" :src="message.mediaUrl" alt="Gemaltes Bild zur Elternfreigabe" />
            </div>

            <div v-else-if="message.type === 'audio' && message.mediaUrl" class="mt-4 rounded-[24px] bg-heart-cream p-4">
              <audio class="w-full" controls :src="message.mediaUrl" />
            </div>

            <div v-else-if="message.type === 'video' && message.mediaUrl" class="mt-4 rounded-[24px] bg-heart-cream p-4">
              <video class="max-h-[32rem] w-full rounded-2xl bg-black" controls :src="message.mediaUrl" />
            </div>

            <div v-else class="mt-4 rounded-[24px] bg-heart-cream p-4 text-lg font-bold leading-8 text-heart-ink">
              {{ message.content }}
            </div>
          </article>
        </div>
        <div class="grid grid-cols-2 gap-3 lg:w-56 lg:grid-cols-1">
          <button class="focus-ring inline-flex min-h-16 items-center justify-center gap-2 rounded-2xl bg-heart-leaf px-5 font-black text-white" type="button" @click="approveMessage(message.id)">
            <Check class="h-6 w-6" aria-hidden="true" />
            Freigeben
          </button>
          <button class="focus-ring inline-flex min-h-16 items-center justify-center gap-2 rounded-2xl bg-rose-700 px-5 font-black text-white" type="button" @click="rejectMessage(message.id)">
            <X class="h-6 w-6" aria-hidden="true" />
            Ablehnen
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
